import * as React from "react";
import { Toast } from "react-ui";
import { KonturColors } from "@skbkontur/colors";

import { UnitId } from "../../../../Unit/UnitId/UnitId";
import { AbsoluteResolvedModelPath } from "../../../../../../../../Common/ModelPath/ModelPath";
import { MenuItem } from "../../MenuItem/MenuItem";
import { PushInstance } from "../../../../../Internals/ControlContexts/PathContexts/PushInstance";
import { VerticalSlideInCollapsibleBlock } from "../VerticalSlideInCollapsibleBlock/VerticalSlideInCollapsibleBlock";
import { NestedLevelContext } from "../../../NestedLevelContext";
import { NestedItems } from "../NestedItems.styles";
import { MenuKebab } from "../../../MenuKebab";
import { PropsPageActions } from "../../../../Unit/Types";
import { getMenuItemInfosKey } from "../../../UnitItemHelpers";
import { getFromLocalStorage } from "../../../../../../Engine/Core/Store/LocalStorageUtils";
import { Counter } from "../../../../Panel/Tabs/Counter";

export interface MenuItemChildInstancesProps {
    id: UnitId;
    instance: string;
    parentInstances?: string[];
    isCurrent: boolean;
    label: string;
    index: number;
    onSelectInstance: () => void;
    onAddInstance: () => void;
    onCopyInstance: () => Promise<void>;
    onClearInstance: () => void;
    onRemoveInstance: () => void;
    isHideRemoveAction?: boolean;
    pageActions?: PropsPageActions;
    instanceTitle: string;
    children?: React.ReactNode;
    isReadOnly?: boolean;
    hasSingleChild?: boolean;
    isChildItemActive?: boolean;
    unitPath: AbsoluteResolvedModelPath;
    isOnlyPlus: boolean;
    disableAppend?: boolean;
    setCollapsedUnitItem?: (
        isCollapsed: boolean,
        fufDraftId?: string,
        instance?: string,
        instances?: string[],
        name?: string
    ) => void;
    fufDraftId: string;
    name?: string;
    hideActions: boolean;
    extraInfo?: string;
    errorsCount: number;
    showErrorsCount?: boolean;
}

export function MenuItemChildInstance(props: MenuItemChildInstancesProps): JSX.Element {
    const [collapsed, setCollapsed] = React.useState<boolean>(
        getFromLocalStorage<boolean>(
            getMenuItemInfosKey(props.id.pageId, props.fufDraftId, props.instance, props.parentInstances, props.name),
            false
        )
    );
    const nestedLevel = React.useContext(NestedLevelContext);
    const handleAddClick = () => {
        props.onAddInstance();
    };

    const handleCopyClick = async () => {
        Toast.push("Раздел скопирован");
        await props.onCopyInstance();
    };

    const handleClearClick = () => {
        props.onClearInstance();
    };
    const handleRemoveClick = () => {
        props.onRemoveInstance();
    };

    const handleClick = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        props.onSelectInstance();
    }, []);

    const getActions = () => {
        if (props.isReadOnly || props.isOnlyPlus || props.hideActions) {
            return null;
        }
        return (
            <MenuKebab
                data-page-id={props.id.pageId}
                pageActions={props.pageActions}
                onAddInstance={props.disableAppend ? undefined : handleAddClick}
                onCopyInstance={props.disableAppend ? undefined : handleCopyClick}
                onClearInstance={handleClearClick}
                onRemoveInstance={props.isHideRemoveAction ? undefined : handleRemoveClick}
            />
        );
    };

    const showLabel = props.showErrorsCount && props.errorsCount > 0;
    const label = showLabel && <Counter count={props.errorsCount} backgroundColor={KonturColors.mainErrorShapeRed60} />;

    return (
        <MenuItem
            id={props.id}
            data-tid="Instance"
            data-page-id={props.id.pageId}
            collapsible={!props.hasSingleChild}
            collapsed={collapsed}
            onCollapseChange={setCollapsed}
            active={props.isCurrent}
            childActive={props.isChildItemActive}
            onClick={handleClick}
            showActionOnHover={false}
            action={getActions()}
            name={props.instanceTitle}
            index={props.index}
            setCollapsedUnitItem={props.setCollapsedUnitItem}
            currentInstance={props.instance}
            parentInstances={props.parentInstances}
            fufDraftId={props.fufDraftId}
            extraInfo={props.extraInfo}
            label={label}>
            {props.children && (
                <NestedLevelContext.Provider value={nestedLevel + 1}>
                    <VerticalSlideInCollapsibleBlock collapsed={collapsed}>
                        {(className, ref) => (
                            <PushInstance bindingPath={props.unitPath} instance={props.instance}>
                                <div className={className} ref={ref}>
                                    <NestedItems>{props.children}</NestedItems>
                                </div>
                            </PushInstance>
                        )}
                    </VerticalSlideInCollapsibleBlock>
                </NestedLevelContext.Provider>
            )}
        </MenuItem>
    );
}
